import * as THREE from "three";
import { MeshoptSimplifier } from "meshoptimizer/simplifier";
import { seamSafe } from "./lodSeams";

/**
 * Reduced-detail copies of static scenery geometry, made once per source geometry and reused for every copy of it.
 *
 * The art ships one detail level, budgeted for the prop standing next to the road (tools/art/simplify_props.mjs: a
 * tree is ~10 k triangles). The same tree out in the haze beyond the map does not need them: that land was ~1.7 M
 * triangles a frame. Simplifying at load keeps one art set (no second texture set to download and hold, no second
 * material to draw it with) and adapts to whatever the kit ships.
 *
 * `Permissive` lets the simplifier collapse across UV and normal seams — without it these kits, whose pieces are
 * split along every seam, stall at 45-85 %. Collapsing a UV seam leaves triangles with corners in two texture charts,
 * which drew the atlas gutter between them (dark patches on the far canopies, R5); lodSeams.ts puts every such
 * triangle back inside one chart, adding a vertex where a corner has no twin there.
 */
export class Lod {
  /** Whether the simplifier's WebAssembly has compiled. Until then (and where it cannot), everything stays full detail. */
  static ready = false;
  private static readonly cache = new WeakMap<object, Map<string, Reduction | null>>();

  /** Starts compiling the simplifier (at module load, below), so it is ready long before a level bakes its scenery. */
  static init(): void {
    MeshoptSimplifier.ready.then(() => { Lod.ready = MeshoptSimplifier.supported; }, () => { Lod.ready = false; });
  }

  /**
   * The reduction of an indexed geometry to `ratio` of its triangles, within `error` (relative to the mesh's
   * extent). `key` identifies the topology: every geometry reduced under the same key must have the same vertex
   * order and index (copies of one source, placed differently). Null when it cannot be reduced usefully.
   */
  static reduction(key: object, geo: THREE.BufferGeometry, ratio: number, error: number): Reduction | null {
    if (!Lod.ready || !geo.index) return null;
    let byTarget = Lod.cache.get(key);
    if (!byTarget) { byTarget = new Map(); Lod.cache.set(key, byTarget); }
    const id = `${ratio}|${error}`;
    if (byTarget.has(id)) return byTarget.get(id)!;
    const r = Lod.compute(geo, ratio, error);
    byTarget.set(id, r);
    return r;
  }

  /**
   * Drops a cached reduction once its only user has copied it out for good (towerLod.ts keeps the copy itself, shared
   * by every tower of the source): the reduction's index and kept-vertex list are CPU-only, ~2 MB a level of new
   * tower models in a long session (M31).
   */
  static forget(key: object, ratio: number, error: number): void {
    const byTarget = Lod.cache.get(key);
    if (!byTarget) return;
    byTarget.delete(`${ratio}|${error}`);
    if (byTarget.size === 0) Lod.cache.delete(key);
  }

  /** A new geometry holding only the kept vertices of `geo`, drawn with the reduced index. */
  static apply(geo: THREE.BufferGeometry, r: Reduction): THREE.BufferGeometry {
    const out = new THREE.BufferGeometry();
    for (const name of Object.keys(geo.attributes)) {
      // the UVs the seam repair extended can fall just outside a quantized range: carry them as floats
      if (name === "uv" && r.uv) { out.setAttribute(name, new THREE.BufferAttribute(r.uv.slice(), 2)); continue; }
      const a = geo.getAttribute(name) as THREE.BufferAttribute;
      const s = a.itemSize, src = a.array, Ctor = src.constructor as new (n: number) => THREE.TypedArray;
      const arr = new Ctor(r.keep.length * s);
      for (let i = 0; i < r.keep.length; i++) { const from = r.keep[i] * s; for (let c = 0; c < s; c++) arr[i * s + c] = src[from + c]; }
      out.setAttribute(name, new THREE.BufferAttribute(arr, s, a.normalized));
    }
    out.setIndex(new THREE.BufferAttribute(r.index, 1));
    return out;
  }

  private static compute(geo: THREE.BufferGeometry, ratio: number, error: number): Reduction | null {
    const pos = geo.getAttribute("position");
    const n = pos.count;
    const positions = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { positions[i * 3] = pos.getX(i); positions[i * 3 + 1] = pos.getY(i); positions[i * 3 + 2] = pos.getZ(i); }
    // A rigid rig's parts are one mesh, one bone each. The simplifier treats vertices at the same point as one (its seam
    // handling), so two parts that touch (a flame on its torch bowl, a banner on its bracket) were welded and the reduced
    // mesh had triangles spanning two bones: when one part moved they stretched into wedges and lines across the
    // fortress (the far detail of the Dragonlord and the Moonstone Keep). Nudging each bone's vertices apart by a hair
    // keeps the parts apart; the reduced mesh still draws the source positions.
    const bone = geo.getAttribute("skinIndex");
    if (bone) for (let i = 0; i < n; i++) positions[i * 3] += bone.getX(i) * Lod.BoneNudge;
    const src = geo.index!;
    const indices = new Uint32Array(src.count);
    for (let i = 0; i < src.count; i++) indices[i] = src.getX(i);
    const target = Math.max(3, Math.floor((indices.length * ratio) / 3) * 3);
    const [simplified] = MeshoptSimplifier.simplify(indices, positions, 3, target, error, ["Permissive"]);
    // and whatever still spans two bones goes: on a moving part it is a sliver stretched across the model
    const reduced = bone ? oneBone(simplified, bone) : simplified;
    // not worth a second copy of the buffers unless it removes a real share of the triangles
    if (reduced.length === 0 || reduced.length > indices.length * Lod.MinSaving) return null;
    const uvAttr = geo.getAttribute("uv");
    if (uvAttr) {
      const uv = new Float32Array(n * 2);
      for (let i = 0; i < n; i++) { uv[i * 2] = uvAttr.getX(i); uv[i * 2 + 1] = uvAttr.getY(i); }
      const safe = seamSafe(indices, reduced, positions, uv);
      return { keep: safe.keep, index: n > 0xffff || safe.keep.length > 0xffff ? safe.index : Uint16Array.from(safe.index), uv: safe.uv };
    }
    const remap = new Int32Array(n).fill(-1);
    const keep: number[] = [];
    const index = n > 0xffff ? new Uint32Array(reduced.length) : new Uint16Array(reduced.length);
    for (let i = 0; i < reduced.length; i++) {
      const v = reduced[i];
      if (remap[v] < 0) { remap[v] = keep.length; keep.push(v); }
      index[i] = remap[v];
    }
    return { keep: Uint32Array.from(keep), index, uv: null };
  }
  /** A reduction that keeps more than this share of the triangles is dropped. */
  static readonly MinSaving = 0.8;
  /** Per bone index, how far a rigid part's vertices are told apart for the simplifier (model units; see compute). */
  static readonly BoneNudge = 1e-5;
}

/** The triangles of `index` whose three corners ride one bone (a rigid rig's part). */
function oneBone(index: Uint32Array, bone: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): Uint32Array {
  const out = new Uint32Array(index.length);
  let n = 0;
  for (let t = 0; t < index.length; t += 3) {
    const a = bone.getX(index[t]);
    if (bone.getX(index[t + 1]) !== a || bone.getX(index[t + 2]) !== a) continue;
    out[n++] = index[t]; out[n++] = index[t + 1]; out[n++] = index[t + 2];
  }
  return n === index.length ? index : out.slice(0, n);
}

/**
 * A reduced mesh: `keep` lists the source vertex of each new vertex in order (a seam repair can list one twice),
 * `index` draws the triangles over them, and `uv`, when set, replaces the source UVs (lodSeams.ts extended some).
 */
export interface Reduction { readonly keep: Uint32Array; readonly index: Uint16Array | Uint32Array; readonly uv: Float32Array | null }
Lod.init();
